import { useState, useEffect } from "react";

function formatDate(iso: string): string {
  const d = new Date(iso);
  const months = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
  return `${d.getDate()} ${months[d.getMonth()]} ${d.getFullYear()}`;
}

const TILE_W = 350; // text + horizontal gap (px)
const TILE_H = 70; // line height + vertical gap (px)

// HTML text watermark with grid sized dynamically from the viewport — large
// enough to cover the rotated bounding box at any viewport size or zoom.
// HTML text stays vector in Chromium PDF export, so it prints sharp.
export function DocumentWatermark() {
  const [text, setText] = useState<string>("");
  const [grid, setGrid] = useState<{ rows: number; cols: number }>({ rows: 0, cols: 0 });

  useEffect(() => {
    if (localStorage.getItem("cosx-watermark-suppressed") === "1") return;
    const name = localStorage.getItem("cosx-viewer-name");
    const ts = localStorage.getItem("cosx-viewer-ts");
    if (!name) return;
    const date = ts ? formatDate(ts) : formatDate(new Date().toISOString());
    setText(`CONFIDENTIAL  ·  ${name}  ·  ${date}`);

    const update = () => {
      const w = window.innerWidth;
      const h = window.innerHeight;
      // After -30deg rotation the bounding box grows by sin(30)+cos(30) ≈ 1.37.
      // Use 1.6x of the longer side as safety margin so corners stay covered.
      const span = Math.max(w, h) * 1.6;
      setGrid({
        cols: Math.ceil(span / TILE_W),
        rows: Math.ceil(span / TILE_H),
      });
    };
    update();
    window.addEventListener("resize", update);
    return () => window.removeEventListener("resize", update);
  }, []);

  if (!text || !grid.rows) return null;

  return (
    <div
      className="fixed inset-0 pointer-events-none overflow-hidden"
      style={{ zIndex: 50 }}
      aria-hidden="true"
    >
      <div
        style={{
          position: "absolute",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%) rotate(-30deg)",
          display: "flex",
          flexDirection: "column",
          gap: `${TILE_H - 16}px`,
        }}
      >
        {Array.from({ length: grid.rows }).map((_, r) => (
          <div
            key={r}
            style={{ display: "flex", gap: "60px", whiteSpace: "nowrap" }}
          >
            {Array.from({ length: grid.cols }).map((_, c) => (
              <span
                key={c}
                className="font-[family-name:var(--font-mono)]"
                style={{
                  fontSize: "11px",
                  letterSpacing: "0.1em",
                  color: "var(--color-fg)",
                  opacity: 0.045,
                  userSelect: "none",
                }}
              >
                {text}
              </span>
            ))}
          </div>
        ))}
      </div>
    </div>
  );
}
